
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useSpring } from 'framer-motion';

interface TrailPoint {
  x: number;
  y: number;
  id: number;
}

export default function CustomCursor() {
  const [isHoveringClickable, setIsHoveringClickable] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const posRef = useRef({ x: -100, y: -100 });
  const trailIdRef = useRef(0);
  const lastTrailTimeRef = useRef(0);

  const springX = useSpring(-100, { damping: 25, stiffness: 400, mass: 0.4 });
  const springY = useSpring(-100, { damping: 25, stiffness: 400, mass: 0.4 });

  const handleMouseMove = useCallback((e: MouseEvent) => {
    posRef.current = { x: e.clientX, y: e.clientY };
    springX.set(e.clientX);
    springY.set(e.clientY);
    setIsVisible(true);

    // Add trail points with throttling
    const now = Date.now();
    if (now - lastTrailTimeRef.current > 12) {
      lastTrailTimeRef.current = now;
      trailIdRef.current += 1;
      setTrail((prev) => {
        const newTrail = [
          ...prev,
          { x: e.clientX, y: e.clientY, id: trailIdRef.current },
        ];
        // Keep only last 15 points for the trail
        return newTrail.slice(-15);
      });
    }
  }, [springX, springY]);

  // Clean up old trail points
  useEffect(() => {
    const interval = setInterval(() => {
      setTrail((prev) => {
        if (prev.length === 0) return prev;
        return prev.slice(1);
      });
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleElementHover = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Only show "Open" tag on project cards with data-project-card attribute
      const projectCard = target.closest('[data-project-card]');
      setIsHoveringClickable(!!projectCard);
      
      // Check if it's a coming soon card
      if (projectCard) {
        const comingSoon = projectCard.getAttribute('data-coming-soon') === 'true';
        setIsComingSoon(comingSoon);
      } else {
        setIsComingSoon(false);
      }
    };

    const handleMouseDown = () => setIsPressed(true);
    const handleMouseUp = () => setIsPressed(false);
    const handleMouseLeave = () => {
      setIsVisible(false);
      setTrail([]);
    };
    const handleMouseEnter = () => setIsVisible(true);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseover', handleElementHover);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseover', handleElementHover);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [handleMouseMove]);

  // Hide on touch devices
  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      setIsVisible(false);
    }
  }, []);

  if (!isVisible) return null;

  return (
    <>
      {/* Trail effect - subtle dashes following cursor */}
      {trail.map((point, index) => {
        const opacity = (index + 1) / trail.length * 0.6;
        const scale = (index + 1) / trail.length;
        return (
          <motion.div
            key={point.id}
            className="fixed pointer-events-none z-[9998]"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity, scale }}
            exit={{ opacity: 0, scale: 0 }}
            style={{
              left: point.x,
              top: point.y,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div
              className="rounded-full"
              style={{
                width: 4 + index * 0.4,
                height: 4 + index * 0.4,
                background: '#37352F',
              }}
            />
          </motion.div>
        );
      })}

      {/* Main cursor */}
      <motion.div
        className="fixed pointer-events-none z-[9999]"
        style={{
          x: springX,
          y: springY,
          translateX: '-50%',
          translateY: '-50%',
        }}
      >
        <AnimatePresence mode="wait">
          {isHoveringClickable ? (
            <motion.div
              key={isComingSoon ? "coming-soon-tag" : "notion-tag"}
              initial={{ scale: 0.8, opacity: 0, rotate: -8 }}
              animate={{ scale: 1, opacity: 1, rotate: -6 }}
              exit={{ scale: 0.8, opacity: 0, rotate: -8 }}
              transition={{ type: 'spring', damping: 22, stiffness: 500, mass: 0.3 }}
              className="relative flex items-center gap-1.5"
            >
              {/* Notion-style tag pill */}
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
                style={{
                  background: isComingSoon ? '#6B7280' : '#37352F',
                  boxShadow: isComingSoon 
                    ? '0 2px 8px rgba(107, 114, 128, 0.25), 0 0 0 1px rgba(107, 114, 128, 0.08)'
                    : '0 2px 8px rgba(55, 53, 47, 0.25), 0 0 0 1px rgba(55, 53, 47, 0.08)',
                }}
              >
                <span className="text-[11px] font-medium text-white tracking-wide whitespace-nowrap" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                  {isComingSoon ? 'Coming Soon' : 'Open'}
                </span>
                {!isComingSoon && (
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-[10px] text-white/70"></i>
                  </span>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="notion-dot"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{
                scale: isPressed ? 0.7 : 1,
                opacity: 1,
              }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: 'spring', damping: 22, stiffness: 600, mass: 0.3 }}
              className="relative flex items-center justify-center"
            >
              {/* Outer ring */}
              <motion.div
                className="absolute rounded-full"
                animate={{
                  width: isPressed ? 28 : 36,
                  height: isPressed ? 28 : 36,
                  opacity: isPressed ? 0.15 : 0.08,
                }}
                transition={{ type: 'spring', damping: 22, stiffness: 400, mass: 0.3 }}
                style={{
                  background: '#37352F',
                  transform: 'translate(-50%, -50%)',
                  left: '50%',
                  top: '50%',
                }}
              />
              {/* Inner dot */}
              <motion.div
                className="rounded-full"
                animate={{
                  width: isPressed ? 6 : 8,
                  height: isPressed ? 6 : 8,
                }}
                transition={{ type: 'spring', damping: 22, stiffness: 400, mass: 0.3 }}
                style={{
                  background: '#37352F',
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
